/**
 * LeftRail – Collapsible left panel for Ask AI.
 *
 * Shows:
 *  - "New question" button
 *  - Quick-prompt presets (static)
 *  - Recent questions from session state
 *
 * Visible by default on xl+, hidden on smaller screens  (parent handles via Drawer).
 */
import React from 'react';
import {
    Box,
    Typography,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Divider,
    Button,
} from '@mui/material';
import {
    Add,
    ChevronLeft,
    TrendingUp,
    Shield,
    AccountBalance,
    PieChart,
    Inventory2,
    Architecture,
    BarChart,
    ManageAccounts,
    History,
    DeleteOutline,
} from '@mui/icons-material';

/* ── Preset quick-prompts ── */
const PRESETS = [
    { label: 'Revenue segments', icon: <PieChart sx={{ fontSize: 16 }} />, query: 'Break down the revenue by segment' },
    { label: 'Competitors', icon: <TrendingUp sx={{ fontSize: 16 }} />, query: 'Who are the main competitors and how does this company compare?' },
    { label: 'Key risks', icon: <Shield sx={{ fontSize: 16 }} />, query: 'What are the key risk factors disclosed?' },
    { label: 'Growth drivers', icon: <TrendingUp sx={{ fontSize: 16 }} />, query: 'What are the main growth drivers?' },
    { label: 'Working capital', icon: <Inventory2 sx={{ fontSize: 16 }} />, query: 'Analyze the working capital position' },
    { label: 'Capex', icon: <Architecture sx={{ fontSize: 16 }} />, query: 'What is the capital expenditure trend and guidance?' },
    { label: 'Margin analysis', icon: <BarChart sx={{ fontSize: 16 }} />, query: 'How have operating margins changed over the last 3 years?' },
    { label: 'Management outlook', icon: <ManageAccounts sx={{ fontSize: 16 }} />, query: 'What does management say about future outlook?' },
    { label: 'Debt profile', icon: <AccountBalance sx={{ fontSize: 16 }} />, query: 'Summarize the debt and borrowing profile' },
];

const LeftRail = ({
    onSelectQuestion,
    onNewQuestion,
    onClearThread,
    recentQuestions,
    companyName,
    onClose, // For mobile drawer close
    isMobile,
}) => {
    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'var(--bg-2)',
                borderRight: isMobile ? 'none' : '1px solid var(--border)',
                overflow: 'hidden',
            }}
        >
            {/* ── Header ── */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid var(--border)',
                    minHeight: 48,
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--text-1)', fontSize: '0.82rem', letterSpacing: '0.03em' }}>
                    ASK AI
                </Typography>
                {isMobile && (
                    <IconButton size="small" onClick={onClose}>
                        <ChevronLeft fontSize="small" />
                    </IconButton>
                )}
            </Box>

            {/* ── New question button ── */}
            <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
                <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Add sx={{ fontSize: 16 }} />}
                    onClick={onNewQuestion}
                    sx={{
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        borderColor: 'var(--brand-2)',
                        color: 'var(--brand-2)',
                        borderRadius: 2,
                        py: 0.75,
                        '&:hover': { backgroundColor: 'var(--bg-3)', borderColor: 'var(--brand-2)' },
                    }}
                >
                    New question
                </Button>
            </Box>

            {/* ── Scrollable body ── */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 0.5 }}>
                {/* Quick-prompts */}
                <Typography
                    variant="overline"
                    sx={{ px: 1.5, pt: 1.5, pb: 0.5, display: 'block', color: 'var(--muted)', fontSize: '0.63rem', letterSpacing: '0.08em' }}
                >
                    Quick prompts
                </Typography>
                <List dense disablePadding>
                    {PRESETS.map((p) => (
                        <ListItemButton
                            key={p.label}
                            onClick={() => onSelectQuestion(p.query)}
                            sx={{
                                borderRadius: 1.5,
                                mx: 0.5,
                                mb: 0.25,
                                py: 0.5,
                                '&:hover': { backgroundColor: 'var(--bg-3)' },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 28, color: 'var(--text-2)' }}>
                                {p.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={p.label}
                                primaryTypographyProps={{ fontSize: '0.78rem', color: 'var(--text-2)' }}
                            />
                        </ListItemButton>
                    ))}
                </List>

                {/* Recent questions */}
                {recentQuestions && recentQuestions.length > 0 && (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <Typography
                            variant="overline"
                            sx={{ px: 1.5, pt: 1, pb: 0.5, display: 'block', color: 'var(--muted)', fontSize: '0.63rem', letterSpacing: '0.08em' }}
                        >
                            Recent questions
                        </Typography>
                        <List dense disablePadding>
                            {recentQuestions.slice(0, 15).map((q, idx) => (
                                <ListItemButton
                                    key={idx}
                                    onClick={() => onSelectQuestion(q)}
                                    sx={{
                                        borderRadius: 1.5,
                                        mx: 0.5,
                                        mb: 0.25,
                                        py: 0.5,
                                        '&:hover': { backgroundColor: 'var(--bg-3)' },
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 28, color: 'var(--muted)' }}>
                                        <History sx={{ fontSize: 15 }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={q.length > 60 ? q.substring(0, 57) + '...' : q}
                                        primaryTypographyProps={{ fontSize: '0.76rem', color: 'var(--text-2)' }}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    </>
                )}
            </Box>

            {/* ── Footer ── */}
            <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid var(--border)' }}>
                {onClearThread && (
                    <Button
                        fullWidth
                        size="small"
                        variant="text"
                        startIcon={<DeleteOutline sx={{ fontSize: 15 }} />}
                        onClick={onClearThread}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            color: 'var(--muted)',
                            justifyContent: 'flex-start',
                            mb: 0.75,
                            '&:hover': { color: 'var(--text-2)', backgroundColor: 'var(--bg-3)' },
                        }}
                    >
                        Clear history
                    </Button>
                )}
                <Typography variant="caption" sx={{ color: 'var(--muted)', fontSize: '0.65rem' }}>
                    Powered by RAG + Web Search
                </Typography>
            </Box>
        </Box>
    );
};

export default LeftRail;
